import { useEffect } from 'react'
import { useBusinessContext } from '@root/context'
import { useDebouncedValue } from '@hooks'
import { useQuery } from '@tanstack/react-query'
import {
  fetchDocumentRelationSuggestionPreview,
  fetchDocumentRelationSuggestions
} from '../documentRelations'

interface HookProps {
  documentId: number
}

export interface PreviewHookProps {
  date?: string | null
  contactId?: number | null
  blueprint?: object
  excludeDocumentIds?: number[]
  enabled?: boolean
}

const hasBlueprintAmounts = (blueprint?: object): boolean => {
  if (!blueprint || typeof blueprint !== 'object') {
    return false
  }

  const typedBlueprint = blueprint as {
    debet_entries?: unknown
    credit_entries?: unknown
    expense_entries?: unknown
  }
  const sections = ['debet_entries', 'credit_entries', 'expense_entries']
  return sections.some(section => {
    const entries = typedBlueprint[section as keyof typeof typedBlueprint]
    if (!Array.isArray(entries)) {
      return false
    }

    return entries.some(entry => {
      if (!entry || typeof entry !== 'object') {
        return false
      }
      const amount = (entry as { amount?: unknown }).amount
      if (typeof amount === 'number') {
        return amount !== 0
      }
      if (typeof amount === 'string') {
        const parsedAmount = Number(amount)
        return Number.isFinite(parsedAmount) && parsedAmount !== 0
      }
      return false
    })
  })
}

export const useDocumentRelationSuggestions = ({
  documentId,
  enabled = true
}: HookProps & { enabled?: boolean }) => {
  const { slug } = useBusinessContext()
  return useQuery({
    queryKey: [slug, 'documents', documentId, 'relation-suggestions'],
    queryFn: () => fetchDocumentRelationSuggestions({ slug, documentId }),
    enabled: !!slug && !!documentId && enabled
  })
}

export const useDocumentRelationSuggestionPreview = ({
  date,
  contactId,
  blueprint,
  excludeDocumentIds = [],
  enabled = true
}: PreviewHookProps) => {
  const { slug } = useBusinessContext()
  const blueprintKey = JSON.stringify(blueprint ?? {})
  const { debouncedValue: debouncedBlueprintKey, setValue: setDebouncedBlueprintKey } =
    useDebouncedValue<string>({
      delay: 400,
      initialValue: blueprintKey
    })

  useEffect(() => {
    setDebouncedBlueprintKey(blueprintKey)
  }, [blueprintKey, setDebouncedBlueprintKey])

  const shouldFetch =
    !!slug &&
    !!date &&
    enabled &&
    hasBlueprintAmounts(blueprint) &&
    debouncedBlueprintKey === blueprintKey

  return useQuery({
    queryKey: [
      slug,
      'documents',
      'relation-suggestions-preview',
      date ?? null,
      contactId ?? null,
      debouncedBlueprintKey,
      excludeDocumentIds.join('-')
    ],
    queryFn: () =>
      fetchDocumentRelationSuggestionPreview({
        slug,
        date,
        contact_id: contactId,
        blueprint,
        exclude_document_ids: excludeDocumentIds
      }),
    enabled: shouldFetch,
    staleTime: 1000 * 60,
    refetchOnMount: false
  })
}
